import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Link } from "components/DesignSystem/Link/Link";
import { Loader } from "components/DesignSystem/Loader/Loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { InfoSolid } from "components/InfoSolid";
import { NumericStepper } from "components/NumericStepper/NumericStepper";
import { PriceInput } from "components/PriceInput/PriceInput";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { Cart } from "components/icons/Cart";
import {
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS,
  ACCOUNTING_METHODS_OPTIONS,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { addProductToCartValidation } from "formValidations/addProductToCartValidation";
import { Field, FieldProps, Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import ReactCountryFlag from "react-country-flag";
import { useParams } from "react-router-dom";
import { MultiValue, SingleValue } from "react-select";
import {
  useAddProductToCartMutation,
  useGetAllProductsQuery,
  Product,
} from "store/apis/products";
import { useLazyGetAllTransactionsDataQuery } from "store/apis/transactions";
import { AD_HOC, US_BOOKKEEPING_ONE_TIME } from "types/Models/services";
import { BackendError } from "types/utils/error";
import { getSeasonsFrom } from "utils/getAllSeason";
import { CalculatedAmountForAnnualCatchup } from "./CalculatedAmountForAnnualCatchup";
import { ConditionalAddProductToCartWrapper } from "./ConditionalAddProductToCartWrapper";

export const EntityLabel = ({
  name,
  countryCode,
}: {
  name: string;
  countryCode: string;
}) => (
  <div className="t-flex t-gap-1.5 t-items-center">
    <span className="t-rounded-sm">
      <ReactCountryFlag svg countryCode={countryCode} />
    </span>{" "}
    <span>{name}</span>
  </div>
);

export const AddProductToCart = ({
  onClose,
  productIdFromProps,
  currentSeason,
  entityIdFromProps,
  onAdd,
  useOwnModal,
  setCurrentStep,
  ignoreCountErrors = false,
}: {
  onClose: () => void;
  productIdFromProps?: string;
  currentSeason?: number;
  entityIdFromProps?: string;
  onAdd?: () => void;
  useOwnModal?: boolean;
  setCurrentStep?: (step: number) => void;
  ignoreCountErrors?: boolean;
}) => {
  const { successToast, alertToast } = useToast();
  const group = useCurrentGroupContext();
  const groupId = group.uuid;
  const entityUuid = useCurrentEntityId();
  const entityId = entityIdFromProps || entityUuid;
  const { productId: productUuid } = useParams<{ productId?: string }>();
  const { isCustomer, isAdmin } = useRoleBasedView();
  const productId = productIdFromProps || productUuid;

  const { data: products, isLoading } = useGetAllProductsQuery(
    {
      groupId: groupId!,
      entityId: entityId,
    },
    { skip: !groupId || !entityId }
  );
  const [addProductToCart, { isLoading: addingProductToCart }] =
    useAddProductToCartMutation();

  const [getTransactions, { isFetching: gettingTransactions }] =
    useLazyGetAllTransactionsDataQuery();

  const calculateBookkeepingPrice = async ({
    startDate,
    endDate,
    setPrice,
    accountingMethod,
  }: {
    startDate: string;
    endDate: string;
    setPrice: (price: number) => void;
    accountingMethod: ACCOUNTING_METHOD_TYPES;
  }) => {
    const payload = {
      groupId,
      entityId,
      pageSize: "1",
      page_num: 1,
      startDate: { value: startDate },
      endDate: { value: endDate },
    };

    const transactions = await getTransactions(payload).unwrap();

    if (
      transactions.total_count <= 199 &&
      accountingMethod === ACCOUNTING_METHODS.CASH
    ) {
      return setPrice(199);
    }

    if (
      transactions.total_count <= 399 &&
      accountingMethod === ACCOUNTING_METHODS.ACCRUAL
    ) {
      return setPrice(399);
    }

    if (accountingMethod === ACCOUNTING_METHODS.ACCRUAL) {
      return setPrice(transactions.total_count * 2);
    }

    if (accountingMethod === ACCOUNTING_METHODS.CASH) {
      setPrice(transactions.total_count);
    }
  };

  if (useOwnModal && isLoading) {
    return (
      <Modal.Body className="t-flex t-h-[482px] t-w-full t-items-center t-justify-center">
        <Loader />
      </Modal.Body>
    );
  }

  if (isLoading) {
    return (
      <Modal.Root open>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add to cart</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-flex t-h-[482px] t-w-full t-items-center t-justify-center">
              <Loader />
            </div>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  if (!products) {
    return null;
  }

  const allProducts = Object.values(products).flatMap((productGroup) => {
    return Object.values(productGroup).flatMap((value) => {
      return Array.isArray(value) &&
        value.length > 0 &&
        !("subscription_name" in value[0])
        ? (value as Product[])
        : [];
    });
  });

  const selectedProduct = allProducts.find(
    (p) => p.uuid === productId
  ) as Product;

  const {
    title,
    product_content_type_id,
    uuid,
    seasons,
    quantity_type,
    approx_deadline,
    approx_price,
    description,
    states,
    has_from_date,
    has_accounting_method,
    has_to_date,
    base_task_key,
  } = selectedProduct || {};
  const isAdhoc = base_task_key === AD_HOC;
  const nextYear = dayjs().year() + 1;
  const allSeasons = isAdhoc ? getSeasonsFrom(2021, nextYear) : seasons;

  const currentYearSeason = allSeasons?.find((s) => s === dayjs().year());
  const selectedYear = dayjs().year().toString();

  const onSubmit = async ({
    entity_id,
    season,
    quantity,
    state_id,
    payment_amount,
    from_date,
    to_date,
    accounting_method,
    task_name,
  }: {
    entity_id: string;
    season?: number;
    quantity: number;
    state_id?: string;
    payment_amount?: number;
    from_date?: string | null;
    to_date?: string | null;
    accounting_method?: string;
    task_name?: string;
  }) => {
    if (groupId && uuid && product_content_type_id) {
      try {
        const taskName = task_name ? { task_name } : {};
        const payload = {
          entity_id,
          season: season ? String(season) : null,
          product_content_type_id,
          product_id: uuid,
          quantity: quantity,
          payment_amount,
          state_id,
          from_date: from_date
            ? dayjs(from_date).format(YYYY_MM_DD)
            : undefined,
          to_date: to_date ? dayjs(to_date).format(YYYY_MM_DD) : undefined,
          accounting_method,
          ...taskName,
        };

        await addProductToCart({
          groupId: groupId,
          entityId,
          payload: payload,
        }).unwrap();

        successToast({ title: "Item added!", size: "small" });
        onClose();
        onAdd?.();
      } catch (e) {
        const error = e as BackendError;

        if (
          ignoreCountErrors &&
          error.data?.error?.code === "cart_item_already_exists"
        ) {
          return onAdd?.();
        }

        alertToast({ message: error.data?.error?.message });
      }
    }
  };

  return (
    <ConditionalAddProductToCartWrapper
      useOwnModal={useOwnModal}
      title={title}
      onClose={onClose}
    >
      <Formik
        initialValues={{
          entity_id: entityId,
          product_id: uuid,
          product_content_type_id: product_content_type_id,
          season: currentSeason || currentYearSeason,
          has_season: Boolean(seasons && seasons.length > 0),
          has_states: Boolean(states && states.length > 0),
          has_accounting_method: has_accounting_method,
          has_from_date: has_from_date,
          quantity: 1,
          state_id: "",
          payment_amount: 0,
          from_date: "",
          to_date: "",
          accounting_method: has_accounting_method ? "CASH" : "",
          task_name: "",
        }}
        validationSchema={addProductToCartValidation}
        onSubmit={onSubmit}
      >
        {({ submitForm, values, setFieldValue, touched }) => {
          const selectedEntity = group?.entities?.find(
            (e) => e.uuid === values.entity_id
          );

          const setPrice = (price: number) => {
            setFieldValue("payment_amount", price);
          };

          const onAccountingMethodChange = (
            option: MultiValue<OptionData> | SingleValue<OptionData>
          ) => {
            if (touched.payment_amount) {
              return;
            }

            if (option instanceof Array) {
              return;
            }

            if (base_task_key === US_BOOKKEEPING_ONE_TIME && isAdmin) {
              if (option?.value === ACCOUNTING_METHODS.ACCRUAL) {
                setFieldValue("payment_amount", 399);
              }

              if (option?.value === ACCOUNTING_METHODS.CASH) {
                setFieldValue("payment_amount", 199);
              }
            }
          };

          const onSeasonChange = (
            option: MultiValue<OptionData> | SingleValue<OptionData>
          ) => {
            if (option instanceof Array) {
              return;
            }

            if (!option?.value) {
              return;
            }

            const fromDate = dayjs()
              .year(Number(option.value))
              .startOf("year")
              .format(YYYY_MM_DD);

            const toDate = dayjs()
              .year(Number(option.value || 0))
              .endOf("year")
              .format(YYYY_MM_DD);

            if (has_from_date) {
              setFieldValue("from_date", fromDate);
            }

            if (has_to_date) {
              setFieldValue("to_date", toDate);
            }
          };

          return (
            <>
              <Modal.Body>
                <Form className="t-m-0">
                  <div className="t-grid t-grid-cols-2 t-gap-4">
                    <ToolTip text="Change entity from top right of the page">
                      <div>
                        <Combobox
                          isDisabled
                          components={{
                            DropdownIndicator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          name="entity_id"
                          withForm
                          label="Entity"
                          value={
                            selectedEntity && {
                              label: (
                                <EntityLabel
                                  name={selectedEntity.name}
                                  countryCode={selectedEntity.country_code}
                                />
                              ),
                              value: selectedEntity.uuid,
                            }
                          }
                          options={group.entities?.map((entity) => ({
                            label: (
                              <EntityLabel
                                name={entity.name}
                                countryCode={entity.country_code}
                              />
                            ),
                            value: entity.uuid,
                          }))}
                        />
                      </div>
                    </ToolTip>

                    {allSeasons && allSeasons.length > 0 && (
                      <Combobox
                        required
                        menuPortalTarget={document.body}
                        name="season"
                        withForm
                        label="Season"
                        onChange={onSeasonChange}
                        value={
                          Boolean(values.season)
                            ? {
                                value: String(values.season),
                                label: values.season,
                              }
                            : null
                        }
                        options={allSeasons?.map((season: number) => ({
                          value: String(season),
                          label: season,
                        }))}
                      />
                    )}

                    {isAdhoc && (
                      <TextInput
                        name="task_name"
                        label="Task name"
                        placeholder="Enter task name"
                      />
                    )}
                    {has_from_date && (
                      <>
                        <Field name="from_date">
                          {({ field }: FieldProps) => {
                            return (
                              <DateInput
                                {...field}
                                label="Start from"
                                placeholder="Select month and year"
                                dateFormat="MMMM-yyyy"
                                showMonthYearPicker
                                required
                                portalId="startDate"
                                openToDate={new Date(selectedYear || "")}
                                maxDate={dayjs(selectedYear)
                                  .endOf("year")
                                  .toDate()}
                              />
                            );
                          }}
                        </Field>
                      </>
                    )}

                    {has_to_date && (
                      <Field name="to_date">
                        {({ field }: FieldProps) => {
                          return (
                            <DateInput
                              {...field}
                              label="To"
                              placeholder="Select month and year"
                              dateFormat="MMMM-yyyy"
                              showMonthYearPicker
                              required
                              portalId="startDate"
                              minDate={new Date(values.from_date)}
                              openToDate={new Date(selectedYear || "")}
                              maxDate={dayjs(selectedYear)
                                .endOf("year")
                                .toDate()}
                            />
                          );
                        }}
                      </Field>
                    )}

                    {has_accounting_method && (
                      <Combobox
                        required
                        withForm
                        label="Accounting method"
                        name="accounting_method"
                        options={ACCOUNTING_METHODS_OPTIONS}
                        onChange={onAccountingMethodChange}
                        value={
                          ACCOUNTING_METHODS_OPTIONS.find(
                            (am) => am.value === values.accounting_method
                          ) || null
                        }
                      />
                    )}

                    {states && !isCustomer && states.length > 0 && (
                      <div className="t-col-span-2">
                        <Combobox
                          menuPortalTarget={document.body}
                          name="state_id"
                          withForm
                          label="State"
                          options={states?.map(({ state_id, name }) => ({
                            value: state_id,
                            label: name,
                          }))}
                        />
                      </div>
                    )}

                    {quantity_type && (
                      <>
                        <div className="t-self-center">
                          <span className="t-font-sans t-text-body-sm t-text-text-30 t-mb-1.5">
                            Number of {quantity_type}
                          </span>
                        </div>

                        <div className="t-justify-self-end">
                          <NumericStepper name="quantity" />
                        </div>
                      </>
                    )}

                    {description && (
                      <div className="t-col-span-2">
                        <span className="t-font-sans t-text-body-sm t-text-text-30 t-mb-1.5">
                          About
                        </span>
                        <div
                          className="t-text-body"
                          dangerouslySetInnerHTML={{
                            __html: description,
                          }}
                        />
                      </div>
                    )}

                    {approx_deadline && (
                      <div>
                        <p className="t-font-sans t-text-body-sm t-text-text-30 t-mb-1.5 t-m-0">
                          Typical Deadline
                        </p>
                        <p className="t-text-subtitle">
                          {dayjs(approx_deadline).format("Do MMMM")}
                        </p>
                      </div>
                    )}

                    {(approx_price && !isNaN(Number(approx_price))) ||
                    isCustomer ? (
                      <div
                        className={classNames({
                          "t-col-span-2":
                            base_task_key === US_BOOKKEEPING_ONE_TIME,
                        })}
                      >
                        <p className="t-font-sans t-text-body-sm t-text-text-30 t-mb-1.5 t-m-0">
                          Price
                        </p>
                        {base_task_key === US_BOOKKEEPING_ONE_TIME ? (
                          <>
                            {currentSeason ? (
                              <CalculatedAmountForAnnualCatchup
                                currentSeason={currentSeason}
                                calculatePrice={() =>
                                  calculateBookkeepingPrice({
                                    startDate: values.from_date,
                                    endDate: values.to_date,
                                    accountingMethod:
                                      values.accounting_method as ACCOUNTING_METHOD_TYPES,
                                    setPrice,
                                  })
                                }
                              />
                            ) : (
                              <div>
                                <p className="t-text-subtitle">Post-paid</p>
                                <div className="t-flex t-gap-1 t-text-text-30 t-text-body-sm">
                                  <InfoSolid size="12" />
                                  <span>
                                    The price will be calculated based on the
                                    accounting method selected and no.of
                                    transactions during the service period.{" "}
                                    <Link to="https://www.inkle.io/pricing#Inkle%20Books">
                                      Know more
                                    </Link>
                                  </span>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="t-text-subtitle">
                            <AmountSuperScript
                              amount={Number(approx_price || "0")}
                            />
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="t-col-span-2 t-flex t-gap-2 t-items-end">
                        <PriceInput
                          name="payment_amount"
                          label="Price"
                          type="number"
                          required
                        />
                        {base_task_key === US_BOOKKEEPING_ONE_TIME && (
                          <ConditionalToolTip
                            condition={
                              values.from_date &&
                              values.to_date &&
                              values.accounting_method
                                ? "Calculate price based on number of transactions"
                                : "Please select start date, end date and accounting method to calculate price"
                            }
                          >
                            <span>
                              <Button
                                disabled={
                                  !Boolean(
                                    values.from_date &&
                                      values.to_date &&
                                      values.accounting_method
                                  )
                                }
                                isLoading={gettingTransactions}
                                type="button"
                                onClick={() =>
                                  calculateBookkeepingPrice({
                                    startDate: values.from_date,
                                    endDate: values.to_date,
                                    accountingMethod:
                                      values.accounting_method as ACCOUNTING_METHOD_TYPES,
                                    setPrice,
                                  })
                                }
                              >
                                Calculate
                              </Button>
                            </span>
                          </ConditionalToolTip>
                        )}
                      </div>
                    )}
                  </div>
                </Form>
              </Modal.Body>

              <Modal.Footer>
                <div
                  className={classNames("t-flex", {
                    "t-justify-between": Boolean(setCurrentStep),
                    "t-justify-end": !Boolean(setCurrentStep),
                  })}
                >
                  {setCurrentStep && (
                    <Button onClick={() => setCurrentStep(2)}>Back</Button>
                  )}
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={addingProductToCart}
                    disabled={addingProductToCart}
                  >
                    <div className="t-flex t-items-center t-gap-1.5">
                      <Cart />
                      <span>Add to Cart</span>
                    </div>
                  </Button>
                </div>
              </Modal.Footer>
            </>
          );
        }}
      </Formik>
    </ConditionalAddProductToCartWrapper>
  );
};
